<template>
  <vs-form ref="invoiceForm">
    <vs-text-input
      :value="formData.batchNumber"
      label="Batch Number"
      required
      autofocus
      @input="setInvoiceNumber"
    />
    <vs-date-picker
      :value="formData.batchDate"
      label="Batch Date"
      autofocus
      required
      can-select-past
      :enable-time="false"
      @input="setInvoiceDate"
    />
    <vs-autocomplete
      :value="formData.paymentTerms"
      label="Payment Terms"
      :items="PAYMENT_TERMS_OPTIONS"
      @input="setPaymentTerms"
    />
    <vs-date-picker
      v-if="formData.paymentTerms != null"
      :disabled="formData.paymentTerms !== BatchPaymentTerms.Custom"
      :value="formData.dueDate"
      label="Due Date"
      autofocus
      can-select-past
      :enable-time="false"
      required
      @input="setDueDate"
    />
    <vs-text-input :value="formData.memo" label="Memo" multiline @input="setMemo" />

    <div v-for="syncTarget in batchSyncTargets" :key="syncTarget.id" class="sync-target-form">
      <batch-sync-target-custom-fields
        v-if="syncTarget.customFieldsSchemaId !== null"
        :title="syncTarget.name"
        :app-id="syncTarget.id"
        :value="syncTarget.customData"
        :schema-version-id="syncTarget.customFieldsSchemaId"
        :ref="(el) => (customFieldsRefs[syncTarget.id] = el)"
        @dirty="handleCustomFieldsEdited"
      />
    </div>

    <vs-button
      label="Save"
      :loading="saving"
      :disabled="isCreateMode ? false : !hasChanges"
      full-width
      @click="save"
    />
  </vs-form>
</template>

<script setup lang="ts">
import {
  BatchFormData,
  BatchFormSyncTarget,
  BatchPaymentTerms,
  PAYMENT_TERMS_OPTIONS,
} from '@/models/batch'
import moment from 'moment/moment'
import { computed, reactive, ref, watchEffect } from 'vue'
import { CustomDataWithSchema } from '@/api/invoice'
import { getDueDate } from '@/utils/invoice-due-date'
import VsAutocomplete from '@/components/vision/Inputs/VsAutocomplete/VsAutocomplete.vue'
import VsForm from '@/components/vision/VsForm.vue'
import VsTextInput from '@/components/vision/Inputs/VsTextInput.vue'
import VsDatePicker from '@/components/vision/Inputs/VsDatePicker.vue'
import VsButton from '@/components/vision/VsButton.vue'
import BatchSyncTargetCustomFields from '@/components/batches/BatchSyncTargetCustomFields.vue'
import { useFeatureFlagsQuery } from '@/api/composables/feature-flags'

interface FormData {
  id?: string | null
  batchNumber?: string | null
  batchDate?: moment.Moment | null
  paymentTerms?: BatchPaymentTerms | null
  dueDate?: moment.Moment | null
  memo?: string | null
}

const formData = reactive<FormData>({
  id: undefined,
  batchNumber: undefined,
  batchDate: undefined,
  paymentTerms: undefined,
  dueDate: undefined,
  memo: undefined,
})

const props = withDefaults(
  defineProps<{
    batch: BatchFormData
    batchSyncTargets: BatchFormSyncTarget[]
    saving: boolean
    isCreateMode?: boolean
  }>(),
  {
    isCreateMode: false,
  },
)

const customFieldsRefs = ref<{
  [syncTargetId: string]: InstanceType<typeof BatchSyncTargetCustomFields>
}>({})

const hasChanges = computed(() => {
  // check for changes in custom fields
  if (customFieldsDirty.value) {
    return true
  }

  // check for changes in core fields
  return (
    formData.batchNumber !== props.batch?.invoiceNumber ||
    !areDatesEqual(formData.batchDate, props.batch ? moment.utc(props.batch.invoiceDate) : null) ||
    formData.paymentTerms !== props.batch.paymentTerms ||
    !areDatesEqual(
      formData.dueDate,
      props.batch && props.batch.dueDate ? moment.utc(props.batch?.dueDate) : null,
    ) ||
    formData.memo !== props.batch.memo
  )
})

const customFieldsDirty = ref(false)
const handleCustomFieldsEdited = (dirty: boolean) => {
  customFieldsDirty.value = dirty
}

function areDatesEqual(
  date1: moment.Moment | null | undefined,
  date2: moment.Moment | null | undefined,
) {
  if (!date1 && !date2) {
    return true
  }
  if (date1 && date2) {
    return date1.isSame(date2)
  }
  return false
}

watchEffect(() => {
  formData.id = props.batch.id
  formData.batchNumber = props.batch.invoiceNumber
  formData.batchDate = moment.utc(props.batch.invoiceDate).local()
  formData.paymentTerms = props.batch.paymentTerms
  formData.dueDate = props.batch.dueDate ? moment.utc(props.batch.dueDate).local() : null
  formData.memo = props.batch.memo
})

const invoiceForm = ref()
const emit = defineEmits<{
  (
    e: 'save-clicked',
    value: {
      id: string
      customDataPerApp: Record<string, CustomDataWithSchema>
      invoiceNumber: string | null | undefined
      invoiceDate: moment.Moment | undefined
      paymentTerms: BatchPaymentTerms | null
      dueDate: moment.Moment | undefined
      jobIds: undefined
      memo: string | null | undefined
    },
  )
}>()

function setInvoiceNumber(batchNumber: string) {
  formData.batchNumber = batchNumber
}

function setInvoiceDate(batchDate: moment.Moment) {
  formData.batchDate = batchDate
  if (formData.paymentTerms != null) {
    formData.dueDate = getDueDate(formData.batchDate, formData.paymentTerms)
  }
}

function setDueDate(dueDate: moment.Moment) {
  formData.dueDate = dueDate
}

function setPaymentTerms(newPaymentTerms: BatchPaymentTerms | null) {
  formData.paymentTerms = newPaymentTerms
  if (formData.paymentTerms != null && formData.batchDate) {
    formData.dueDate = getDueDate(formData.batchDate, formData.paymentTerms)
  } else {
    formData.dueDate = null
  }
}

function setMemo(newMemo: string | null) {
  formData.memo = newMemo
}

async function save() {
  // validate the main form
  if (!invoiceForm.value.isValid()) return

  const keyedCustomData: Record<string, CustomDataWithSchema> = {}

  // validate each sync target's custom schema
  for (const syncTarget of props.batchSyncTargets ?? []) {
    const form = customFieldsRefs.value[syncTarget.id]
    if (!form) {
      continue
    }

    if (!form.validate()) {
      return
    }
    const formData = form.save()
    keyedCustomData[syncTarget.id] = { data: formData.data, schema_id: formData.schemaId }
  }

  emit('save-clicked', {
    id: props.batch.id,
    customDataPerApp: keyedCustomData,
    invoiceNumber: formData.batchNumber,
    invoiceDate: formData.batchDate?.utc(),
    paymentTerms: formData.paymentTerms ?? null,
    dueDate: formData.dueDate?.utc(),
    jobIds: undefined,
    memo: formData.memo,
  })
}
</script>
<style scoped>
.sync-target-form {
  margin-top: 2rem;
  margin-bottom: 2rem;
}
</style>
